<template>
  <div
    class="file-uploader"
    :class="{'file-uploader--upload-screenshot': uploadScreenshot}">
    <label
      v-if="(!progress || !files.length) && !error"
      class="file-uploader__drop-area file-uploader__block"
      :class="{
        'file-uploader__drop-area--dragover': isDragOver,
        'file-uploader__drop-area--upload-screenshot': uploadScreenshot,
      }"
      @dragover.prevent="dragOver"
      @dragleave.prevent="dragLeave"
      @drop.prevent="handleFilesChange">
      <input
        type="file"
        :accept="acceptType"
        :multiple="multiple"
        hidden
        @change="handleFilesChange">

      <span class="file-uploader__download">
        <img
          v-if="!uploadScreenshot"
          width="16px"
          height="16px"
          src="/images/icons/upload.svg"
          alt="download">

        <img
          v-else
          width="24px"
          height="24px"
          src="/images/icons/upload-bold.svg"
          alt="download">
      </span>

      <p class="file-uploader__title">
        {{ isDragOver ? 'Release to upload file' : 'Drag & drop here or click to upload' }}
      </p>

      <p
        v-if="!uploadScreenshot"
        class="file-uploader__description">
        XLS
      </p>
    </label>

    <div
      v-else-if="(progress > 0 && !error)"
      class="file-uploader__block"
      :class="{
        'file-uploader__drop-area--upload-screenshot': uploadScreenshot,
      }">
      <div class="file-uploader__progress">
        <spinner-progress class="file-uploader__progress-block" />

        <span class="file-uploader__progress-text">
          {{ progress }}%
        </span>
      </div>

      <p
        v-if="truncatedFileName"
        class="file-uploader__description">
        {{ truncatedFileName }}
      </p>
    </div>

    <div
      v-else-if="error"
      class="file-uploader__block">
      <p class="file-uploader__smile">
        🤯
      </p>

      <p class="file-uploader__title">
        Oops! Smth went wrong
      </p>

      <p class="file-uploader__description">
        {{ error }}
      </p>

      <p class="file-uploader__description">
        {{ truncatedFileName }}
      </p>

      <button
        class="button button-additional file-uploader__button"
        @click="clear">
        Try again
      </button>
    </div>
  </div>
</template>

<script>
import AppModel from 'Models/AppModel';
import { stringTruncate } from 'Root/helpers/tools';
import SpinnerProgress from 'Components/SpinnerProgress.vue';

export default {
  name: 'FileUploader',

  components: { SpinnerProgress },

  props: {
    settingId: {
      type: [Number, String],
      default: null,
    },

    guaranteeTime: {
      type: [Number, String],
      default: null,
    },

    uploadScreenshot: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      uploadingFile: null,
      files: [],
      progress: 0,
      error: null,
      isDragOver: false,
    };
  },

  computed: {
    truncatedFileName() {
      if (!this.uploadingFile) {
        return '';
      }

      return stringTruncate(this.uploadingFile.name, 40);
    },

    acceptType() {
      return this.uploadScreenshot ? 'image/jpeg,image/png,image/jpg' : 'application/vnd.ms-excel';
    },

    apiUrl() {
      return this.uploadScreenshot
        ? `${AppModel.API_URI}/currency_marketplace/offers/upload_screenshot`
      // eslint-disable-next-line max-len
        : `${AppModel.API_URI}/currency_marketplace/offers/${this.settingId}/import?guarantee_delivery_time=${this.guaranteeTime}`;
    },
  },

  methods: {
    handleFilesChange(event) {
      const isDragEvent = event instanceof DragEvent;
      const files = isDragEvent ? event.dataTransfer.files : event.target.files;

      if (this.isDragOver) {
        this.isDragOver = false;
      }

      if (files.length > 0) {
        this.files = files;
        this.uploadFiles();
      }
    },

    dragOver() {
      this.isDragOver = true;
    },

    dragLeave() {
      this.isDragOver = false;
    },

    async uploadFiles() {
      try {
        await [...this.files].reduce(async (previousPromise, file) => {
          await previousPromise;
          return this.uploadFile(file);
        }, Promise.resolve());
      } catch (e) {
        console.warn('Upload stopped due to error:', e);
      } finally {
        this.files = [];
      }
    },

    async uploadFile(file) {
      this.uploadingFile = file;

      const allowedExtensions = this.uploadScreenshot
        ? ['jpeg', 'png', 'jpg']
        : ['xls'];

      const fileExtension = file.name.slice(Math.max(0, file.name.lastIndexOf('.') + 1));

      if (allowedExtensions.includes(fileExtension.toLowerCase())) {
        const formData = new FormData();
        formData.append('file', file);

        await this.$axios.post(this.apiUrl, formData, {
          onUploadProgress: (progressEvent) => {
            this.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          },
        })
          .then((response) => {
            if (this.uploadScreenshot) {
              this.$emit('screenshot-uploaded', `/storage${response.data.result.uri}`);
              this.clear();
            } else {
              this.$vuedals.close(response.data);
            }
          })
          .catch((error) => {
            console.warn({ error });
            this.error = 'Error uploading file';
            throw error;
          });
      } else {
        this.error = 'Invalid file extension';
        throw new Error('Invalid file extension');
      }
    },

    clear() {
      this.uploadingFile = null;
      this.progress = 0;
      this.error = null;
    },
  },
};
</script>

<style lang="scss">
@media screen and (min-width: 601px) {
  .excel-import-popup {
    .booster-popup {
      max-width: 715px;
      width: 100%;
      background-color: $color--basic-black;
      padding: 57px 48px 48px;
    }
  }
}
</style>

<style lang="scss" scoped>
.file-uploader {
  width: 100%;
  background: radial-gradient(circle,
          rgba($color--basic-blue, .5) 30%,
          rgba($color--basic-blue, .4) 30%,
          rgba($color--basic-blue, .4) 45%,
          rgba($color--basic-blue, .3) 45%,
          rgba($color--basic-blue, .3) 60%,
          rgba($color--basic-blue, .2) 60%,
          rgba($color--basic-blue, .2) 75%,
          rgba($color--basic-blue, .1) 75%,
  );
  border: 1px dashed $color--basic-dark-gray;
  border-radius: 24px;
}

.file-uploader--upload-screenshot {
  border: 3px dashed $color--basic-dark-gray;
}

.file-uploader__block {
  display: grid;
  text-align: center;
  justify-content: center;
  align-content: center;
  grid-gap: 20px;
  min-height: 300px;
  border-radius: inherit;
}

.file-uploader__title {
  font-size: 25px;
  font-weight: 500;
}

.file-uploader__download {
  border-radius: 50%;
  padding: 12px;
  width: fit-content;
  line-height: 0;
  margin: auto;
  background: linear-gradient(270deg, $color--basic-blue 0%, $color--basic-purple 100%);
}

.file-uploader__drop-area {
  cursor: pointer;
}

.file-uploader__drop-area--dragover {
  background: rgba($color--basic-blue, 0.15);
}

.file-uploader__drop-area--upload-screenshot {
  min-height: 300px;
}

.file-uploader__description {
  font-size: 15px;
}

.file-uploader__smile {
  font-size: 28px;
}

.file-uploader__button {
  width: max-content;
  padding: 15px;
  margin: auto;
}

.file-uploader__progress {
  position: relative;
}

.file-uploader__progress-text {
  font-size: 35px;
  font-weight: 500;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
